// File: src/components/auth/ProtectedRoute.tsx
// Purpose: Protected Route component with role-based access control
// InstaDev - Protected Route

import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { RootState } from '@/store'
import { UserRole } from '@/lib/rbac/roles'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: string[] // userType values: 'admin', 'staff', 'employee', 'client'
  requiredUserRoles?: UserRole[] // UserRole enum values
  requireAuth?: boolean
  fallbackPath?: string
}

export function ProtectedRoute({
  children,
  requiredRoles = [],
  requiredUserRoles = [],
  requireAuth = true,
  fallbackPath = '/login'
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, userType, userId } = useSelector((state: RootState) => state.auth)

  // Check if authentication is required and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  // If no specific roles are required, allow access for authenticated users
  if (requiredRoles.length === 0 && requiredUserRoles.length === 0) {
    return <>{children}</>
  }

  // Check userType-based access
  if (requiredRoles.length > 0 && userType) {
    const normalizedUserType = userType.toLowerCase()
    const hasRequiredRole = requiredRoles.some(role => role.toLowerCase() === normalizedUserType)
    if (!hasRequiredRole) {
      // Redirect based on user's actual role
      if (normalizedUserType === 'admin') {
        return <Navigate to="/dashboard" replace />
      } else if (normalizedUserType === 'staff' || normalizedUserType === 'employee') {
        return <Navigate to="/staff" replace />
      } else {
        return <Navigate to="/login" replace />
      }
    }
  }

  // For UserRole enum-based checking, we'd need to get the user's role from unified-auth
  // For now, we'll use a mapping from userType to UserRole
  if (requiredUserRoles.length > 0) {
    const userTypeToRoleMapping: Record<string, UserRole> = {
      'admin': UserRole.ADMIN,
      'staff': UserRole.DEVELOPER, // Default staff to developer role
      'employee': UserRole.DEVELOPER,
      'client': UserRole.DEVELOPER
    }

    const currentUserRole = userType ? userTypeToRoleMapping[userType.toLowerCase()] : null
    if (!currentUserRole || !requiredUserRoles.includes(currentUserRole)) {
      // Redirect based on user's actual role
      const normalizedUserType = userType?.toLowerCase()
      if (normalizedUserType === 'admin') {
        return <Navigate to="/dashboard" replace />
      } else if (normalizedUserType === 'staff' || normalizedUserType === 'employee') {
        return <Navigate to="/staff" replace />
      } else {
        return <Navigate to="/login" replace />
      }
    }
  }

  return <>{children}</>
}


// Role-specific protected route components for convenience
export function AdminRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={['admin']}>
      {children}
    </ProtectedRoute>
  )
}

export function StaffRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={['employee', 'staff']}>
      {children}
    </ProtectedRoute>
  )
}

export function EmployeeRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={['employee']}>
      {children}
    </ProtectedRoute>
  )
}

export function ClientRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={['client']}>
      {children}
    </ProtectedRoute>
  )
}